import React from "react";

import { useLocation } from "react-router-dom";
import classNames from "classnames";

import type { Node, Plan } from "../../types/explain";

import { Diff, Diffable, diff } from "../../utils/diff";
import { extractSubplans, findPlanIndexes, reduce } from "../../utils/explain";
import { useSelectedNode } from "components/WithExplainNodeSelection";
import Button from "components/Button";
import Callout from "components/Callout";
import { labelAspect, useExplainAspect } from "components/WithExplainAspect";
import ExplainFormattedMetric from "components/ExplainFormattedMetric";

type LabeledPlan = {
  label: string;
  plan: Plan;
};

export const ExplainDiff = ({
  planA,
  planB,
  blockSize,
}: {
  planA: LabeledPlan;
  planB: LabeledPlan;
  blockSize: number;
}) => {
  const planAIndexes = findPlanIndexes(planA.plan);
  const planBIndexes = findPlanIndexes(planB.plan);
  const allIndexes = Array.from(
    new Set(Array.from(planAIndexes).concat(Array.from(planBIndexes))),
  ).sort();
  const diffableA = toDiffablePlan(planA.plan, allIndexes);
  const diffableB = toDiffablePlan(planB.plan, allIndexes);

  function comparePlans(a: DiffablePlanLine, b: DiffablePlanLine) {
    return (
      a.line.trimStart() === b.line.trimStart() &&
      a.node["Index Name"] === b.node["Index Name"] &&
      a.node["Relation Name"] === b.node["Relation Name"] &&
      a.node.Relation === b.node.Relation &&
      a.node["CTE Name"] === b.node["CTE Name"]
    );
  }
  const rootDiff = diff(diffableA.root, diffableB.root, comparePlans);

  const subPlans = getComparableSubplans(diffableA, diffableB);
  const subplanDiffs = Object.entries(subPlans).map(
    ([name, [subplanA, subplanB]]) => {
      return [
        name,
        diff(subplanA ?? [], subplanB ?? [], comparePlans),
      ] as const;
    },
  );

  const hasDiff =
    rootDiff.some((val) => val.kind != "equal") ||
    subplanDiffs.some(([_subPlanName, diff]) => {
      return diff.some((val) => val.kind != "equal");
    });

  const [aspect, subAspect] = useExplainAspect();

  return (
    <div>
      {!hasDiff && (
        <Callout className="mb-3" thin>
          Both plans have an identical structure
        </Callout>
      )}
      <table className="w-full table-fixed border-separate">
        <tbody>
          <tr>
            <th className="overflow-hidden text-ellipsis w-1/3 text-base align-top">
              Plan A
              <span className="mb-1 block text-sm text-[#666679] pr-2">
                {planA.label}
              </span>
            </th>
            <th className="overflow-hidden text-ellipsis w-1/3 text-base align-top">
              Plan B
              <span className="mb-1 block text-sm text-[#666679] pr-2">
                {planB.label}
              </span>
            </th>
            <th className="overflow-hidden text-ellipsis text-right w-1/6 text-base align-top">
              Plan A
              <span className="mb-1 block text-sm text-[#666679]">
                {labelAspect(aspect, subAspect)}
              </span>
            </th>
            <th className="overflow-hidden text-ellipsis text-right w-1/6 text-base align-top">
              Plan B
              <span className="mb-1 block text-sm text-[#666679]">
                {labelAspect(aspect, subAspect)}
              </span>
            </th>
          </tr>
          <SubplanDiff planDiff={rootDiff} blockSize={blockSize} />
          {subplanDiffs.map(([name, subplanDiff]) => {
            return (
              <React.Fragment key={name}>
                <tr>
                  <th className="pt-2 overflow-hidden text-ellipsis">{name}</th>
                </tr>
                <SubplanDiff planDiff={subplanDiff} blockSize={blockSize} />
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

function getComparableSubplans(planA: DiffablePlan, planB: DiffablePlan) {
  // We need to diff Plan A's subplans with Plan B's subplans, but we don't want
  // to just rely on the source ordering to avoid comparing unrelated subplans
  // if they are reordered. Use subplan names to match these instead.
  function mapSubPlansByName(
    map: { [name: string]: DiffableSubPlan },
    subPlan: DiffableSubPlan,
    i: number,
  ) {
    const name =
      subPlan[0].content.node["Subplan Name"] ?? `Unknown Subplan ${i}`;
    map[name] = subPlan;
    return map;
  }
  const subPlansAbyName = planA.subPlans.reduce(mapSubPlansByName, {});
  const subPlansBbyName = planB.subPlans.reduce(mapSubPlansByName, {});
  const allSubplanNames = new Set(
    Object.keys(subPlansAbyName).concat(Object.keys(subPlansBbyName)),
  );
  const subPlans = Array.from(allSubplanNames).reduce(
    (map, name) => {
      const comparablePair = [
        subPlansAbyName[name] ?? [],
        subPlansBbyName[name] ?? [],
      ] as const;
      map[name] = comparablePair;
      return map;
    },
    {} as {
      [name: string]: readonly [
        planASubplan: DiffableSubPlan,
        planBSubplan: DiffableSubPlan,
      ];
    },
  );

  return subPlans;
}

function SubplanDiff({
  planDiff,
  blockSize,
}: {
  planDiff: Diff<DiffablePlanLine>;
  blockSize: number;
}) {
  const { hash } = useLocation();

  const [aspect, subAspect] = useExplainAspect();
  const usingComparisonPlan = hash.includes("compare");
  const [selectedNode, setSelected] = useSelectedNode();
  const selectedId = selectedNode?.extra.id;

  return (
    <>
      {planDiff.map((edit, idx) => {
        const aSelected =
          !usingComparisonPlan &&
          edit.kind != "insert" &&
          edit.a.content.node.extra.id === selectedId;
        const bSelected =
          usingComparisonPlan &&
          edit.kind != "delete" &&
          edit.b.content.node.extra.id === selectedId;
        switch (edit.kind) {
          case "insert":
            return (
              <tr
                className="hover:bg-slate-100 font-mono whitespace-pre"
                key={idx}
              >
                <td></td>
                <DiffCell
                  variant="insert"
                  onSelected={() =>
                    setSelected(edit.b.content.node.extra.id, "comparison")
                  }
                  selected={bSelected}
                  line={edit.b.content}
                />
                <td className="text-right"></td>
                <td className="text-right">
                  <ExplainFormattedMetric
                    node={edit.b.content.node}
                    aspect={aspect}
                    subAspect={subAspect}
                    blockSize={blockSize}
                  />
                </td>
              </tr>
            );
          case "delete":
            return (
              <tr
                className="hover:bg-slate-100 font-mono whitespace-pre"
                key={idx}
              >
                <DiffCell
                  variant="delete"
                  onSelected={() =>
                    setSelected(edit.a.content.node.extra.id, "main")
                  }
                  selected={aSelected}
                  line={edit.a.content}
                />
                <td></td>
                <td className="text-right">
                  <ExplainFormattedMetric
                    node={edit.a.content.node}
                    aspect={aspect}
                    subAspect={subAspect}
                    blockSize={blockSize}
                  />
                </td>
                <td className="text-right"></td>
              </tr>
            );
          case "equal":
            return (
              <tr
                className="hover:bg-slate-100 font-mono whitespace-pre"
                key={idx}
              >
                <DiffCell
                  variant="equal"
                  onSelected={() =>
                    setSelected(edit.a.content.node.extra.id, "main")
                  }
                  selected={aSelected}
                  line={edit.a.content}
                />
                <DiffCell
                  variant="equal"
                  onSelected={() =>
                    setSelected(edit.b.content.node.extra.id, "comparison")
                  }
                  selected={bSelected}
                  line={edit.b.content}
                />
                <td className="text-right">
                  <ExplainFormattedMetric
                    node={edit.a.content.node}
                    aspect={aspect}
                    subAspect={subAspect}
                    blockSize={blockSize}
                  />
                </td>
                <td className="text-right">
                  <ExplainFormattedMetric
                    node={edit.b.content.node}
                    aspect={aspect}
                    subAspect={subAspect}
                    blockSize={blockSize}
                  />
                </td>
              </tr>
            );
        }
      })}
    </>
  );
}

const DiffCell = ({
  variant,
  onSelected,
  selected,
  line,
}: {
  onSelected: () => void;
  selected: boolean;
  variant: "insert" | "delete" | "equal";
  line: DiffablePlanLine;
}) => {
  const cellStyles =
    variant === "equal"
      ? ""
      : variant === "insert"
      ? "bg-green-200"
      : "bg-red-200";

  const unselectedStyles =
    variant === "equal"
      ? "border-transparent"
      : variant === "insert"
      ? "border-green-200"
      : "border-red-200";

  const detailStyles =
    variant === "equal"
      ? "text-gray-500"
      : variant === "insert"
      ? "text-[#4F725B]"
      : "text-[#765656]";

  return (
    <td
      className={classNames(
        "overflow-hidden border-l-4",
        cellStyles,
        selected ? "border-link" : unselectedStyles,
      )}
    >
      <Button bare className="group w-full text-left flex" onClick={onSelected}>
        <div className="flex-shrink-0 max-w-full overflow-hidden text-ellipsis">
          {line.line}
        </div>
        <div
          className={classNames(
            detailStyles,
            "flex-shrink overflow-hidden text-ellipsis group-hover:text-inherit",
          )}
        >
          {line.detail}
        </div>
      </Button>
    </td>
  );
};

export type DiffablePlanLine = {
  line: string;
  detail?: React.ReactNode;
  node: Node;
};

export type DiffableSubPlan = Diffable<DiffablePlanLine>;

export type DiffablePlan = {
  root: DiffableSubPlan;
  subPlans: DiffableSubPlan[];
};

export function toDiffablePlan(plan: Plan, allIndexes: string[]): DiffablePlan {
  const preparedPlan = extractSubplans(plan);

  function subplanToDiffable(
    state: DiffablePlanLine[],
    node: Node,
    depth: number,
  ) {
    const prefix = " ".repeat(depth);
    const index = node["Index Name"];

    const detail: React.ReactNode[] = [];
    if (index) {
      detail.push(<sup>{allIndexes.indexOf(index) + 1}</sup>);
    }
    const scanTarget =
      "CTE Name" in node
        ? String(node["CTE Name"])
        : "Function Name" in node
        ? String(node["Function Name"]) + "(…)"
        : "Relation Name" in node
        ? String(node["Relation Name"])
        : undefined;
    if (scanTarget) {
      const detailText = `on ${scanTarget}`;
      detail.push(
        <span title={detailText} className="pl-2">
          {detailText}
        </span>,
      );
    }

    return state.concat([
      {
        line: `${prefix}-> ${node["Node Type"]}`,
        detail,
        node,
      },
    ]);
  }

  const rootLines = reduce(preparedPlan.root, subplanToDiffable, []).map(
    (content, idx) => {
      return {
        n: idx,
        content,
      };
    },
  );
  const otherLines = preparedPlan.subPlans.map((subPlan) => {
    return reduce(subPlan, subplanToDiffable, []).map((content, idx) => {
      return {
        n: idx,
        content,
      };
    });
  });

  return {
    root: rootLines,
    subPlans: otherLines,
  };
}

export default ExplainDiff;
